import { Component, inject, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { AuthenticationService } from '@core/auth/authentication.service';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import { PageHeaderComponent } from '../../../../../shared/components/common/page-header/page-header.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ProspectosService } from './prospectos.service';
import { ErrorService } from '../../../../../shared/services/error.service';
import { Prospecto, ProspectoFiltros } from './prospectos.interface';
import { Badge } from '../../../../../shared/components/ui/badge/badge';
import { DeleteTrigger } from '../../../../../shared/components/ui/delete-trigger/delete-trigger';
import { SnackbarService } from '@shared/services/snackbar.service';
import { CombosService } from '../../../../../shared/services/combos.service';
import {
  Pagination,
  SearchInput,
  FilterSelect,
  FilterOption,
  DataTable,
  TableColumn,
  TableCellDirective,
  FiltersContainer
} from '../../../../../shared/components/ui/list-components';

@Component({
  selector: 'app-prospectos',
  imports: [
    HasPermissionDirective,
    PageHeaderComponent,
    Badge,
    DeleteTrigger,
    Pagination,
    SearchInput,
    FilterSelect,
    DataTable,
    TableCellDirective,
    FiltersContainer,
    DatePipe
  ],
  templateUrl: './prospectos.html'
})
export class Prospectos implements OnInit {
  readonly permissionAuth = inject(AuthenticationService);

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private prospectosService = inject(ProspectosService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private combosService = inject(CombosService);

  prospectos: Prospecto[] = [];
  loading = false;
  deleting = false;
  deletingIndex: number | null = null;

  // Paginación
  currentPage = 1;
  lastPage = 1;
  perPage = 15;
  total = 0;

  // Filtros
  filtros: ProspectoFiltros = {
    busqueda: '',
    tipo: '',
    es_cliente: '',
    canal_captacion_id: ''
  };

  // Columnas de la tabla
  columns: TableColumn[] = [
    { key: 'razon_social', header: 'Razón social' },
    { key: 'tipo', header: 'Tipo' },
    { key: 'fecha_solicitud', header: 'Fecha solicitud' },
    { key: 'canal_captacion', header: 'Canal de captación' },
    { key: 'contacto', header: 'Contacto' },
    { key: 'estado', header: 'Estado' },
    { key: 'acciones', header: 'Acciones' }
  ];

  // Opciones de filtros
  tipoOptions: FilterOption[] = [
    { value: 'F', label: 'Persona física' },
    { value: 'J', label: 'Persona jurídica' }
  ];

  estadoOptions: FilterOption[] = [
    { value: 'false', label: 'Pendiente' },
    { value: 'true', label: 'Es cliente' }
  ];

  canalesCaptacionOptions: FilterOption[] = [];

  ngOnInit(): void {
    this.loadCanalesCaptacion();
    this.getProspectos();
  }

  getProspectos(page: number = 1): void {
    this.loading = true;
    this.prospectosService.index(page, this.perPage, this.filtros).subscribe({
      next: (response) => {
        if (response.status) {
          this.prospectos = response.data.data;
          this.currentPage = response.data.pagination.current_page;
          this.lastPage = response.data.pagination.last_page;
          this.total = response.data.pagination.total;
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  onSearchChange(value: string): void {
    this.filtros.busqueda = value;
    this.currentPage = 1;
    this.getProspectos();
  }

  onTipoChange(value: string | number): void {
    this.filtros.tipo = value as string;
    this.currentPage = 1;
    this.getProspectos();
  }

  onEstadoChange(value: string | number): void {
    this.filtros.es_cliente = value as string;
    this.currentPage = 1;
    this.getProspectos();
  }

  onCanalCaptacionChange(value: string | number): void {
    this.filtros.canal_captacion_id = value as string;
    this.currentPage = 1;
    this.getProspectos();
  }

  loadCanalesCaptacion(): void {
    this.combosService.getCanalesCaptacionCombo().subscribe({
      next: (response) => {
        if (response.status) {
          this.canalesCaptacionOptions = response.data.map((canal) => ({
            value: canal.id.toString(),
            label: canal.nombre
          }));
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  get hasActiveFilters(): boolean {
    return Object.values(this.filtros).some(valor => valor !== null && valor !== undefined && valor !== '');
  }

  clearFilters(): void {
    this.filtros = {
      busqueda: '',
      tipo: '',
      es_cliente: '',
      canal_captacion_id: ''
    };
    this.currentPage = 1;
    this.getProspectos();
  }

  onPageChange(page: number): void {
    this.getProspectos(page);
  }

  onPerPageChange(perPage: number): void {
    this.perPage = perPage;
    this.currentPage = 1;
    this.getProspectos(1);
  }

  onRowClick(event: { item: Prospecto; index: number }): void {
    this.view(event.item.id);
  }

  create(): void {
    this.router.navigate(['new'], { relativeTo: this.route });
  }

  view(id: number): void {
    this.router.navigate(['./', id], { relativeTo: this.route });
  }

  delete(id: number, index: number): void {
    this.deleting = true;
    this.deletingIndex = index;

    this.prospectosService.destroy(id).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Prospecto eliminado correctamente');
          this.getProspectos();
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.deleting = false;
      this.deletingIndex = null;
    });
  }
}
